/**
 * DyoMinicart Web Component
 * ============================================================
 * Finalidade: Exibir variações visuais no drawer do carrinho (minicart)
 * com base em modelo preditivo e experimentação via Statsig.
 *
 * Estrutura:
 * - Usa StatsigExperimentManager para verificar gates e acessar parâmetros da layer.
 * - Usa ModelManager para buscar o modelo via API /pull/.
 * - Salva o modelo em localStorage para persistência.
 * - Aplica alterações visuais com base nas flags retornadas pelo modelo.
 *
 * Observabilidade:
 * - Usa console.log para pontos de verificação no DOM.
 * - Envia falhas para Rollbar com contexto (stable_id, gate, layer, etc).
 *
 * Particularidades:
 * - Exibe banner promocional e ativa novo layout com base nas flags.
 */

export {};

import {
  ModelManager,
  getSafeStorage,
  getStatsigClient,
  getCartItemsIdsString,
  getOrCreateStatsigManager,
  buildModelRequestPayload,
  shouldSendPayload,
  PayloadField,
  Logger,
  syncDebugFlag
} from './statsig-manager.js';

// Definição de tipos para a resposta do modelo
interface modelResponse {
  show_washbag?: boolean;
  show_new_layout?: boolean;
  [key: string]: any;
}

const OLD_MODEL_KEY = "dyo_test_minicart_model_data";
const MODEL_KEY = "statsig:test:dyo:minicart";
const EXPERIMENT = {
  title: "DyoMinicart",
  name: "dyo-minicart-layout-v1",
  gate: "dyo-minicart-layout",
  layer: "dyo-minicart-layout",
};

const DEBUG = false; // Altere para false para desativar logs deste teste

if (!customElements.get("dyo-minicart"))
  customElements.define(
    "dyo-minicart",
    class DyoMinicart extends HTMLElement {
      customerId?: string;
      cartItemsIds?: string;
      statsigGate?: string;
      statsigLayer?: string;
      statsigStableId: string | null;
      allocatedExperiment: string | null;
      modelUrl: string | null;
      REQUEST_TIMEOUT_MS: number;
      modelManager: ModelManager;      
      objExperimentInfos: {
        stable_id: string;
        allocated_experiment: string;
        gate: string;
        layer: string;
        cart: string;
        customer_id: string;
        category: string;
        component: string;
      };

      constructor() {
        super();
        this.customerId = this.getAttribute("data-customer-id") || null;
        this.statsigGate = this.getAttribute("data-statsig-gate") || EXPERIMENT.gate;
        this.statsigLayer = this.getAttribute("data-statsig-layer") || EXPERIMENT.layer;
        this.statsigStableId = null;
        this.allocatedExperiment = EXPERIMENT.name;
        this.modelUrl = null;
        this.REQUEST_TIMEOUT_MS = 10000;
        this.modelManager = new ModelManager();
        this.objExperimentInfos = {
          stable_id: this.statsigStableId,
          gate: this.statsigGate,
          layer: this.statsigLayer,
          cart: getCartItemsIdsString(),
          customer_id: this.customerId,
          allocated_experiment: this.allocatedExperiment,
          category: "Statsig",
          component: `${EXPERIMENT.name}`,
        }
      }

      async connectedCallback(): Promise<void> {
        try {
          syncDebugFlag(EXPERIMENT.name, DEBUG);
          const migrated = this.migrateLegacyModelIfNeeded();
          await getOrCreateStatsigManager();
          this.runTest(migrated);
        } catch (error) {
          Logger.error(EXPERIMENT.name, `[Statsig][${EXPERIMENT.name}] StatsigManager not ready`, error, this.objExperimentInfos);
        }
      }

      // Função principal que executa o fluxo do experimento
      async runTest(migrated?: boolean): Promise<void> {
        Logger.log(EXPERIMENT.name, `[Statsig][${EXPERIMENT.name}] Executando runTest(): `, EXPERIMENT);
        try {
          const stableContext = await getStatsigClient()?.getAsyncContext();
          this.statsigStableId = stableContext?.stableID ?? null;
         
          const persistedModelData = this.loadModelFromStorage();
          if (persistedModelData) {
            Logger.log(EXPERIMENT.name, `[Statsig][${EXPERIMENT.name}] Reusing persisted model group data: `, persistedModelData);
            this.applyLayoutChanges(persistedModelData.show_washbag, persistedModelData.show_new_layout);
          }

          const experimentManager = await getOrCreateStatsigManager();
          const gateOpen: boolean = await experimentManager.checkGate(this.statsigGate ?? "");
          const shouldPersist = gateOpen || migrated;

          if (!shouldPersist) {
            Logger.warn(EXPERIMENT.name, `[Statsig][${EXPERIMENT.name}] Gate closed and no migration. Exiting without persisting.`, this.objExperimentInfos);
            return;
          }

          const triggers = ["first_load", "stable_id"];
          const shouldSend = shouldSendPayload(triggers, MODEL_KEY, "every");
          if (!shouldSend) {
            Logger.warn(EXPERIMENT.name, `[Statsig][${EXPERIMENT.name}] Not available to send payload. Aborting...`, triggers, this.objExperimentInfos);
            return;
          }
      
          const layer = await experimentManager.getLayerParams(this.statsigLayer ?? "");
          if (!layer) {
            Logger.warn(EXPERIMENT.name, `[Statsig][${EXPERIMENT.name}] Layer not found. Exiting.`, this.objExperimentInfos);
            return;
          }
      
          this.modelUrl = layer.get("modelUrl");
      
          if (!this.modelUrl) {
            Logger.error(EXPERIMENT.name, `[Statsig][${EXPERIMENT.name}] No model URL found. Exiting.`, this.objExperimentInfos);
            return;
          }

          const includeFields: PayloadField[] = [
            "customer_id",
            "stable_id",
            "allocated_experiment",
            "cart",
            "utms",
            "products",
            "collections"
          ];
      
          const payload = buildModelRequestPayload(
            {
              customer_id: this.customerId ?? null,
              stable_id: this.statsigStableId ?? undefined,
              allocated_experiment: this.allocatedExperiment,
            }, includeFields
          );

          Logger.log(EXPERIMENT.name, `[Statsig][${EXPERIMENT.name}] Payload to request:`, payload);

          const modelData = await this.modelManager.getModel(
            this.modelUrl,
            payload,
            { timeout: this.REQUEST_TIMEOUT_MS },
            MODEL_KEY,
            includeFields,
          ) as modelResponse;
      
          Logger.log(EXPERIMENT.name, `[Statsig][${EXPERIMENT.name}] Applying layout using new data received from model group at first time:`, modelData);
          this.applyLayoutChanges(modelData?.show_washbag, modelData?.show_new_layout);
        } catch (error) {
          Logger.error(EXPERIMENT.name, `[Statsig][${EXPERIMENT.name}] Error in runTest()`, error, this.objExperimentInfos);
        }
      }

      // Aplica as alterações visuais no layout do minicart conforme flags do modelo
      private applyLayoutChanges(showWashbag: boolean, showNewLayout: boolean): void {
        if (showWashbag) {
          // Exibe o banner da washbag, aguardando o elemento estar disponível no DOM
          this.waitForElement(".drawer__cart-items--banner-img").then((el) => {
            (el as HTMLElement).style.display = "block";
            Logger.log(EXPERIMENT.name, `[Statsig][${EXPERIMENT.name}] Washbag banner displayed successfully`);
          }).catch((error) => {
            Logger.warn(EXPERIMENT.name, `[Statsig][${EXPERIMENT.name}] Failed to apply washbag banner: `, error, this.objExperimentInfos);
          });
        }

        if (showNewLayout) {
          // Adiciona classe para aplicar novo layout no drawer do carrinho
          const cartDrawer = document.querySelector("#shopify-section-cart-drawer");
          if (cartDrawer) {
            cartDrawer.classList.add("running-test");
            Logger.log(EXPERIMENT.name, `[Statsig][${EXPERIMENT.name}] New layout applied successfully`);
          } else {
            Logger.warn(EXPERIMENT.name, `[Statsig][${EXPERIMENT.name}] Failed to apply layout: element not found`, this.objExperimentInfos);
          }
        }
      }

      // Carrega os dados do modelo persistidos no localStorage, retornando null se não existir ou inválido
      private loadModelFromStorage(): modelResponse | null {
        const storage = getSafeStorage();
        let parsed = null;
        try {
          const raw = storage.getItem(MODEL_KEY);
          parsed = raw ? JSON.parse(raw) : null;
        } catch {
          parsed = null;
        }

        if (!parsed || typeof parsed !== "object") {
          return null;
        }

        return parsed;
      }

      // Função auxiliar para aguardar a existência de um elemento no DOM com timeout
      private waitForElement(selector: string, timeout: number = 500): Promise<Element> {
        return new Promise((resolve, reject) => {
          const intervalTime = 100;
          let timeElapsed = 0;

          const interval = setInterval(() => {
            const el = document.querySelector(selector);
            if (el) {
              clearInterval(interval);
              resolve(el);
            } else if ((timeElapsed += intervalTime) >= timeout) {
              clearInterval(interval);
              reject(new Error(`[Statsig][${EXPERIMENT.name}] Element ${selector} not found within ${timeout}ms`));
            }
          }, intervalTime);
        });
      }

      // Migração automática do valor antigo, se existir
      private migrateLegacyModelIfNeeded(): boolean {
        try {
          const storage = getSafeStorage();
          const legacyData = storage.getItem(OLD_MODEL_KEY);
          const parsedLegacy = legacyData ? JSON.parse(legacyData) : null;

          if (parsedLegacy && !storage.getItem(MODEL_KEY)) {
            storage.setItem(MODEL_KEY, JSON.stringify(parsedLegacy));

            const stableId = storage.getItem("STATSIG_LOCAL_STORAGE_STABLE_ID");
            if (stableId) {
              storage.setItem(`${MODEL_KEY}:stableid`, stableId);
            }

            Logger.log(EXPERIMENT.name, `[Statsig][${EXPERIMENT.name}] Legacy model data migrated from ${OLD_MODEL_KEY} to ${MODEL_KEY}`);
            return true;
          }
          if (legacyData && storage.getItem(MODEL_KEY)) {
            Logger.log(EXPERIMENT.name, `[Statsig][${EXPERIMENT.name}] Possible rollback detected: ${OLD_MODEL_KEY} still exists`);
          }
        } catch (error) {
          Logger.error(EXPERIMENT.name, `[Statsig][${EXPERIMENT.name}] Failed to migrate legacy model data`, error, this.objExperimentInfos);
        }
        return false;
      }
    }
  );
